import { motion, useReducedMotion } from "framer-motion";
import { ArrowDown, ArrowUp } from "lucide-react";
import { useCountUp } from "../../hooks/useCountUp";
import { fadeIn, fadeUp } from "../../lib/motion";
import { Skeleton } from "../ui/skeleton";

// UI_STANDARDS.md B6/B7 + UX_MOTION.md "Stat cards": SOLID surface (never glass), count-up
// value, staggered fade+rise on mount, delta uses --pos/--neg (never scarlet, never a solid-red
// block per B0 — a negative delta is red TEXT with an arrow, not a red-filled card).
export function StatCard({ label, value, formatValue = (v) => v, delta, subtitle, index = 0, loading, testId, onClick }) {
  const reducedMotion = useReducedMotion();
  const animatedValue = useCountUp(loading ? null : value);

  if (loading) {
    return (
      <div className="solid-surface rounded-[14px] p-5" data-testid={testId}>
        <Skeleton className="h-3 w-24 mb-3 shimmer" />
        <Skeleton className="h-8 w-32 shimmer" />
      </div>
    );
  }

  const clickableProps = onClick
    ? {
        role: "button",
        tabIndex: 0,
        onClick,
        onKeyDown: (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick(e); } },
        className: "solid-surface rounded-[14px] p-5 cursor-pointer transition-transform hover:scale-[1.015] scarlet-focusable",
      }
    : { className: "solid-surface rounded-[14px] p-5" };

  return (
    <motion.div {...fadeUp(reducedMotion, index * 0.04)} data-testid={testId} {...clickableProps}>
      <p className="eyebrow mb-2">{label}</p>
      <p className="text-2xl font-bold tabular-nums-force" style={{ color: "var(--scarlet-text)", letterSpacing: "-0.02em" }}>
        {formatValue(animatedValue)}
      </p>
      {delta != null && (
        <motion.p
          {...fadeIn(reducedMotion, index * 0.04 + 0.08)}
          className="text-xs font-medium mt-1.5 flex items-center gap-1"
          style={{ color: delta >= 0 ? "var(--scarlet-pos)" : "var(--scarlet-neg)" }}
        >
          {delta >= 0 ? <ArrowUp size={12} /> : <ArrowDown size={12} />}
          {Math.abs(delta).toFixed(1)}%
        </motion.p>
      )}
      {subtitle && (
        <motion.p {...fadeIn(reducedMotion, index * 0.04 + 0.08)} className="text-xs mt-1.5" style={{ color: "var(--scarlet-text-secondary)" }}>
          {subtitle}
        </motion.p>
      )}
    </motion.div>
  );
}
